/**
 * Copy + URL-param parsing for `/login`.
 *
 * Deliberately its OWN module with NO `"use client"` directive: `/login` is a
 * server component and reads `searchParams` there. A plain function exported
 * from a client module is a client *reference*, not a callable — calling one
 * during SSR fails with "a is not a function" — so the pure helpers live here
 * and everything that needs the browser lives in `login-model`.
 *
 * Every string below is verbatim from the live reference (xgodo.com/login). UX
 * parity is strict: exactly two fields, one checkbox, one button and two links —
 * nothing added (no social sign-in, no "sign in with email", no captcha) and
 * nothing dropped.
 *
 * The copy is verbatim including the live page's own typos: "dont have an
 * account?" has no apostrophe and "forgot password?" is lowercase. Both are
 * reproduced as-is rather than tidied, the same call `/about` and `/contact`
 * made — parity is the brief, and a silent copy-edit is a drift nobody asked
 * for.
 */

/* --- Copy (verbatim from the live page) ------------------------------------ */

export const LOGIN_TITLE = "Log In";
export const LOGIN_NO_ACCOUNT = "dont have an account?";
export const LOGIN_SIGNUP_LINK = "Sign Up";

export const LOGIN_LABELS = {
  username: "Username",
  password: "Password",
} as const;

export const LOGIN_REMEMBER_ME = "Remember me";
export const LOGIN_FORGOT = "forgot password?";
export const LOGIN_RESEND = "Resend activation email";

export const LOGIN_SUBMIT = "Login";
export const LOGIN_SUBMITTING = "Logging in…";

/* --- URL params ------------------------------------------------------------ */

/**
 * `?scenario=` → a forced display state, so every state is reviewable without
 * driving the backend:
 *   - `empty`      blank fields (the default, and the real first paint)
 *   - `populated`  both fields carrying values and "Remember me" ticked
 *   - `loading`    mid-submit: inputs locked, progress treatment, no spinner
 *   - `error`      the rejected-credentials alert showing
 *
 * Kept after the bake-off closed, as `/signup`, `/labelling` and `/admin/login`
 * kept theirs: it costs nothing and makes every display state reviewable from
 * the URL.
 */
export type LoginScenario = "populated" | "empty" | "loading" | "error";

export function parseLoginScenario(
  raw: string | string[] | undefined,
): LoginScenario {
  const value = Array.isArray(raw) ? raw[0] : raw;
  switch (value) {
    case "populated":
    case "loading":
    case "error":
      return value;
    default:
      return "empty";
  }
}
